import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Cpu, Thermometer, Drop, Lightbulb, Warning,
  Bell, ShieldCheck, TrendUp, Clock, CaretRight,
} from "@phosphor-icons/react";
import LiveRiskWidget from "@/components/LiveRiskWidget";
import ActiveShipmentsPanel from "@/components/ActiveShipmentsPanel";
import DriverPerformancePanel from "@/components/DriverPerformancePanel";
import ConfigurationRequiredWidget from "@/components/ConfigurationRequiredWidget";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_RANK = { critical: 3, warning: 2, info: 1 };
const SEVERITY_STYLES = {
  critical: { bg: "bg-red-500/10", border: "border-l-red-500", dot: "bg-red-400", text: "text-red-400", label: "Critical" },
  warning:  { bg: "bg-amber-500/10", border: "border-l-amber-500", dot: "bg-amber-400", text: "text-amber-400", label: "Warning" },
  info:     { bg: "bg-zinc-500/10", border: "border-l-zinc-500", dot: "bg-zinc-400", text: "text-zinc-400", label: "Info" },
};

export default function OverviewDashboard({ onViewMap, onViewDevices, onViewShipments, onSelectDevice, onSelectShipment }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/fleet/overview`);
      setOverview(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  // Live updates: refetch overview on any inbound event (debounced)
  const refetchTimer = useRef(null);
  const scheduleRefetch = useCallback(() => {
    if (refetchTimer.current) return;
    refetchTimer.current = setTimeout(() => {
      refetchTimer.current = null;
      fetchOverview();
    }, 800);
  }, [fetchOverview]);

  const handleLive = useCallback((msg) => {
    if (["event.ingested", "alert.created", "device.status_changed"].includes(msg.type)) {
      scheduleRefetch();
    }
  }, [scheduleRefetch]);

  const { status: liveStatus } = useLiveUpdates(handleLive);

  // Polling fallback when WebSocket is not connected
  useEffect(() => {
    if (liveStatus === "connected") return;
    const id = setInterval(fetchOverview, 30_000);
    return () => clearInterval(id);
  }, [liveStatus, fetchOverview]);

  const recentAlerts = overview?.recent_alerts || [];

  // Sort alerts by severity (critical -> warning -> info) then by timestamp desc
  const prioritizedAlerts = useMemo(() => {
    return [...recentAlerts].sort((a, b) => {
      const sa = SEVERITY_RANK[a.severity] || 0;
      const sb = SEVERITY_RANK[b.severity] || 0;
      if (sb !== sa) return sb - sa;
      const ta = a.timestamp || a.created_at || "";
      const tb = b.timestamp || b.created_at || "";
      return tb.localeCompare(ta);
    });
  }, [recentAlerts]);

  const sevCounts = useMemo(() => {
    const c = { critical: 0, warning: 0, info: 0 };
    recentAlerts.forEach((a) => { if (c[a.severity] != null) c[a.severity]++; });
    return c;
  }, [recentAlerts]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-teal-400 font-mono text-sm">Loading fleet overview...</div></div>;
  if (!overview) return null;

  const kpis = [
    { label: "Total Devices", value: overview.total_devices, icon: Cpu, color: "text-teal-400", bg: "bg-teal-400/10" },
    { label: "In Alert", value: overview.devices_in_alert, icon: Warning, color: overview.devices_in_alert > 0 ? "text-red-400" : "text-emerald-400", bg: overview.devices_in_alert > 0 ? "bg-red-400/10" : "bg-emerald-400/10" },
    { label: "Temp Excursions", value: overview.temp_excursions, icon: Thermometer, color: overview.temp_excursions > 0 ? "text-amber-400" : "text-emerald-400", bg: overview.temp_excursions > 0 ? "bg-amber-400/10" : "bg-emerald-400/10" },
    { label: "Humidity Issues", value: overview.humidity_excursions, icon: Drop, color: overview.humidity_excursions > 0 ? "text-amber-400" : "text-emerald-400", bg: overview.humidity_excursions > 0 ? "bg-amber-400/10" : "bg-emerald-400/10" },
    { label: "Seal Breaches", value: overview.light_breaches, icon: Lightbulb, color: overview.light_breaches > 0 ? "text-amber-400" : "text-emerald-400", bg: overview.light_breaches > 0 ? "bg-amber-400/10" : "bg-emerald-400/10" },
    { label: "Total Alerts", value: overview.total_alerts, icon: Bell, color: "text-zinc-400", bg: "bg-zinc-400/10" },
  ];

  const alertBreakdown = overview.alert_breakdown || {};
  const topIssues = overview.top_issues || [];

  return (
    <div data-testid="overview-dashboard" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-zinc-50">Operations Dashboard</h2>
          <LiveStatusPill status={liveStatus} />
        </div>
        <div className="flex gap-2">
          <button onClick={onViewShipments} className="text-xs font-mono text-teal-400 hover:text-teal-300 px-3 py-1 rounded-sm bg-teal-500/5 border border-teal-900/30" data-testid="go-to-shipments-btn">Shipments</button>
          <button onClick={onViewMap} className="text-xs font-mono text-teal-400 hover:text-teal-300 px-3 py-1 rounded-sm bg-teal-500/5 border border-teal-900/30" data-testid="go-to-map-btn">View Map</button>
          <button onClick={onViewDevices} className="text-xs font-mono text-teal-400 hover:text-teal-300 px-3 py-1 rounded-sm bg-teal-500/5 border border-teal-900/30" data-testid="go-to-devices-btn">View Devices</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="bg-zinc-900 border-zinc-800 p-4 hover:bg-zinc-800/50 transition-colors" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className={`p-1.5 rounded-sm ${kpi.bg} w-fit mb-2`}><kpi.icon size={16} weight="duotone" className={kpi.color} /></div>
            <p className="text-2xl font-mono font-semibold text-zinc-50">{kpi.value}</p>
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">{kpi.label}</p>
          </Card>
        ))}
      </div>

      {/* Active Shipments — operations-first surface */}
      <ActiveShipmentsPanel
        onSelectShipment={onSelectShipment}
        onViewAll={onViewShipments}
      />

      {/* Phase 47 — Configuration Required widget. Surfaces shipments
          stuck in draft / configuration_required / ready states so an
          operator can finish setup BEFORE alerts are dispatched. */}
      <ConfigurationRequiredWidget onSelectShipment={onSelectShipment} />

      {/* 3-column: Alert Breakdown | Top Issues | Recent Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Alert Breakdown */}
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Alert Breakdown</h3>
            <Badge variant="outline" className="border-zinc-700 text-xs font-mono">{overview.total_alerts}</Badge>
          </div>
          {Object.keys(alertBreakdown).length === 0 ? (
            <div className="text-center py-6"><ShieldCheck size={28} weight="duotone" className="text-emerald-400 mx-auto mb-2" /><p className="text-xs text-zinc-500">All clear</p></div>
          ) : (
            <div className="space-y-2.5">
              {Object.entries(alertBreakdown).sort((a, b) => b[1] - a[1]).map(([type, count]) => {
                const pct = Math.round((count / (overview.total_alerts || 1)) * 100);
                const color = type.includes("temp") ? "bg-teal-400" : type.includes("light") ? "bg-amber-400" : "bg-red-400";
                return (
                  <div key={type} className="flex items-center gap-3">
                    <span className="text-[10px] text-zinc-400 w-24 shrink-0 font-mono truncate">{type.replace(/_/g, " ")}</span>
                    <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-mono text-zinc-300 w-10 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
          {/* Fleet Health */}
          <div className="mt-5 pt-4 border-t border-zinc-800 space-y-2">
            <HealthBar label="Temp Compliance" value={overview.total_devices - overview.temp_excursions} total={overview.total_devices} />
            <HealthBar label="Seal Integrity" value={overview.total_devices - overview.light_breaches} total={overview.total_devices} />
          </div>
        </Card>

        {/* Top Issues */}
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-4">Top Issues</h3>
          <ScrollArea className="h-[280px]">
            {topIssues.length === 0 ? (
              <p className="text-xs text-zinc-600 text-center py-6">No issues</p>
            ) : (
              <div className="space-y-2">
                {topIssues.map((device, i) => {
                  const hasTemp = device.temperature != null && (device.temperature > 40 || device.temperature < 32);
                  const hasLight = device.light && device.light.toLowerCase() !== "dark";
                  return (
                    <div key={device.device_name || i}
                      className="flex items-center gap-3 p-2.5 rounded-sm bg-zinc-950/50 hover:bg-zinc-800 cursor-pointer transition-colors"
                      onClick={() => device.dataset_id && onSelectDevice?.(device.device_name, device.dataset_id)}>
                      <Warning size={14} weight="duotone" className="text-amber-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-mono font-semibold text-zinc-200 truncate">{device.device_name}</p>
                        <div className="flex gap-2 mt-0.5">
                          {hasTemp && <span className="text-[10px] font-mono text-teal-400">{device.temperature}°F</span>}
                          {hasLight && <span className="text-[10px] font-mono text-amber-400">Light: {device.light}</span>}
                        </div>
                      </div>
                      <CaretRight size={12} className="text-zinc-600" />
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </Card>

        {/* Recent Alerts Feed - severity prioritized */}
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Recent Alerts</h3>
            <span className="text-[10px] text-zinc-600 font-mono">Last 30</span>
          </div>
          {/* Severity counts */}
          <div className="flex items-center gap-1.5 mb-3">
            {["critical", "warning", "info"].map((sev) => {
              const s = SEVERITY_STYLES[sev];
              const count = sevCounts[sev];
              return (
                <span key={sev}
                  className={`flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-mono ${count > 0 ? s.bg : "bg-zinc-950/50"} ${count > 0 ? s.text : "text-zinc-600"}`}
                  data-testid={`alert-severity-count-${sev}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${count > 0 ? s.dot : "bg-zinc-700"}`} />
                  {s.label} {count}
                </span>
              );
            })}
          </div>
          <ScrollArea className="h-[235px]">
            {prioritizedAlerts.length === 0 ? (
              <p className="text-xs text-zinc-600 text-center py-6">No recent alerts</p>
            ) : (
              <div className="space-y-1.5" data-testid="prioritized-alerts">
                {prioritizedAlerts.map((alert, i) => {
                  const sev = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.info;
                  return (
                    <div key={alert.id || i}
                      className={`flex items-start gap-2 p-2 pl-2.5 rounded-sm border-l-2 ${sev.border} ${sev.bg} hover:bg-zinc-800/50 transition-colors`}>
                      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${sev.dot}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] text-zinc-200 truncate">{alert.message}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-[8px] uppercase tracking-wider font-mono font-bold ${sev.text}`}>
                            {sev.label}
                          </span>
                          <Clock size={9} className="text-zinc-600" />
                          <span className="text-[9px] font-mono text-zinc-600">{(alert.timestamp || alert.created_at || "").substring(0, 16)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </Card>
      </div>

      {/* Live Risk widget — full width */}
      <LiveRiskWidget onSelectDevice={onSelectDevice} limit={8} />

      {/* Driver Performance — half width */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DriverPerformancePanel />
      </div>
    </div>
  );
}

function HealthBar({ label, value, total }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 100;
  const color = pct >= 90 ? "bg-emerald-400" : pct >= 60 ? "bg-amber-400" : "bg-red-400";
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-zinc-500">{label}</span>
        <span className="text-[10px] font-mono text-zinc-400">{pct}%</span>
      </div>
      <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function LiveStatusPill({ status }) {
  const styles = {
    connected:    { bg: "bg-teal-500/10",  text: "text-teal-400",  dot: "bg-teal-400",   label: "Live", pulse: true },
    connecting:   { bg: "bg-amber-500/10", text: "text-amber-400", dot: "bg-amber-400",  label: "Connecting", pulse: true },
    disconnected: { bg: "bg-zinc-500/10",  text: "text-zinc-400",  dot: "bg-zinc-500",   label: "Polling", pulse: false },
    idle:         { bg: "bg-zinc-500/10",  text: "text-zinc-500",  dot: "bg-zinc-500",   label: "Offline", pulse: false },
  };
  const s = styles[status] || styles.idle;
  return (
    <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider ${s.bg} ${s.text}`}
      data-testid={`live-status-${status}`} title={`Realtime: ${s.label}`}>
      <span className="relative flex h-1.5 w-1.5">
        {s.pulse && status === "connected" && (
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${s.dot} opacity-60`} />
        )}
        <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${s.dot}`} />
      </span>
      {s.label}
    </span>
  );
}

